import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { Sparkles, GitCompareArrows, ListChecks, PenLine, MessageSquare, ArrowLeft, Save, Lock, LockOpen, Printer, Download, ChevronDown, FileText, Loader2, Check, Search, Minus, Plus } from "lucide-react";
import { Button } from "../components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { ThemeToggle } from "../components/ThemeToggle";
import { InfoWidget } from "../components/InfoWidget";
import { PasswordPrompt } from "../components/PasswordPrompt";
import { Ribbon } from "../components/editor/Ribbon";
import { Canvas } from "../components/editor/Canvas";
import { FindReplace } from "../components/editor/FindReplace";
import { SigningPanel, canSign } from "../components/editor/SigningPanel";
import { ChangesPanel } from "../components/editor/ChangesPanel";
import { AiPanel } from "../components/editor/AiPanel";
import { PayLinkDialog } from "../components/editor/PayLinkDialog";
import { SavedFieldsDialog } from "../components/editor/SavedFieldsDialog";
import { FieldPicker } from "../components/editor/FieldPicker";
import { trackHandler, changesIn, resolveChange, createHistory } from "../lib/track";
import { CommentsPanel, unwrapComment } from "../components/editor/CommentsPanel";
import { TableTools, ImageTools, PdfTools } from "../components/editor/ribbon/ContextTools";
import { LinkDialog, DropdownFieldDialog, SaveTemplateDialog } from "../components/editor/SmallDialogs";
import { SignatureDialog } from "../components/editor/SignatureDialog";
import { ImageDialog, insertImage } from "../components/editor/ImageDialog";
import { PageSetupDialog } from "../components/editor/PageSetupDialog";
import { ProtectDialog, PrintPreview } from "../components/editor/OutputDialogs";
import { trackSelection, setActiveRegion, currentCell, exec, insertText, wrapRange, setHistoryHook } from "../components/editor/cmd";
import { getDoc, writeDoc, getAutosaveMinutes, setAutosaveMinutes, saveTemplate } from "../lib/storage";
import { decryptJSON } from "../lib/crypto";
import { defaultSettings, dims, OV_UI, SIG_PLACEHOLDER, INITIALS_PLACEHOLDER, esc } from "../lib/pages";
import { buildBlob, zipDocs, download, safeName } from "../lib/exporters";
import { APP_VERSION } from "../lib/version";

const readFile = (f) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });

export default function Editor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [rec, setRec] = useState(null);
  const [locked, setLocked] = useState(false);
  const [body, setBody] = useState(null);
  const [settings, setSettingsState] = useState(null);
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState("saved");
  const [lastSaved, setLastSaved] = useState(null);
  const [, tick] = useState(0);
  const [dialog, setDialog] = useState(null);
  const [dialogTab, setDialogTab] = useState(null);
  const [panel, setPanel] = useState(null);
  const [activeCmt, setActiveCmt] = useState(null);
  const [author, setAuthorState] = useState(() => localStorage.getItem("lw.author") || "You");
  const [sigTarget, setSigTarget] = useState(null);
  const [inTable, setInTable] = useState(false);
  const [selImg, setSelImg] = useState(null);
  const [pdfPoint, setPdfPoint] = useState(null);
  const [counts, setCounts] = useState({ words: 0, chars: 0, pages: 1 });
  const [zoom, setZoom] = useState(1);
  const fitWidth = useCallback(() => {
    const el = document.querySelector("[data-testid=editor-canvas]");
    if (!el || !settingsRef.current) return;
    const w = dims(settingsRef.current).w + (window.innerWidth < 640 ? 16 : 48);
    setZoom(Math.max(0.3, Math.min(1, Math.floor(((el.clientWidth - 4) / w) * 100) / 100)));
  }, []);
  const [autosaveMin, setAutosaveMin] = useState(getAutosaveMinutes());
  const [busy, setBusy] = useState("");
  const bodyRef = useRef();
  const headerRef = useRef();
  const footerRef = useRef();
  const recRef = useRef(null);
  const pwRef = useRef(null);
  const dirtyRef = useRef(false);
  const settingsRef = useRef(null);
  const titleRef = useRef("");
  const countT = useRef();

  const openBody = (b) => {
    const s = { ...defaultSettings(), ...(b.settings || {}) };
    settingsRef.current = s;
    setSettingsState(s);
    setBody(b);
  };

  useEffect(() => {
    (async () => {
      const d = await getDoc(id);
      if (!d) { toast.error("Document not found"); return nav("/"); }
      recRef.current = d;
      titleRef.current = d.title;
      setRec(d);
      setTitle(d.title);
      setLastSaved(d.updatedAt);
      if (d.encrypted) setLocked(true);
      else openBody(d.body);
    })();
  }, [id, nav]);

  const recount = useCallback(() => {
    const el = bodyRef.current;
    if (!el || !settingsRef.current) return;
    const c = el.cloneNode(true);
    c.querySelectorAll("select").forEach((s) => s.replaceWith(s.options[s.selectedIndex]?.text || ""));
    c.querySelectorAll(".sig-field").forEach((s) => s.remove());
    const txt = c.textContent.replace(/\u200B/g, "");
    setCounts({ words: txt.split(/\s+/).filter(Boolean).length, chars: txt.replace(/\n/g, "").length, pages: Math.max(1, Math.ceil(el.scrollHeight / dims(settingsRef.current).ch - 0.05) + el.querySelectorAll(".page-break").length), sigTodo: el.querySelectorAll('.sig-field:not([data-signed="1"])').length });
  }, []);

  useLayoutEffect(() => {
    if (!body) return;
    bodyRef.current.innerHTML = body.html || "<p><br></p>";
    headerRef.current.innerHTML = settingsRef.current.header || "";
    footerRef.current.innerHTML = settingsRef.current.footer || "";
    document.execCommand("defaultParagraphSeparator", false, "p");
    setActiveRegion(bodyRef.current);
    recount();
  }, [body, recount]);

  useEffect(() => trackSelection(), []);
  useEffect(() => {
    if (!body) return;
    const t = setTimeout(() => window.innerWidth < 1024 && fitWidth(), 60);
    const h = () => window.innerWidth < 1024 && fitWidth();
    window.addEventListener("resize", h);
    window.addEventListener("orientationchange", h);
    return () => { clearTimeout(t); window.removeEventListener("resize", h); window.removeEventListener("orientationchange", h); };
  }, [body, settings?.size, settings?.orientation, fitWidth]);

  const markDirty = useCallback(() => {
    dirtyRef.current = true;
    setStatus("dirty");
    clearTimeout(countT.current);
    countT.current = setTimeout(recount, 400);
  }, [recount]);

  const setSettings = (s) => { settingsRef.current = s; setSettingsState(s); markDirty(); };
  const authorRef = useRef(author);
  authorRef.current = author;

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const hist = createHistory(el);
    const on = () => !!settingsRef.current?.trackChanges;
    const h = trackHandler(el, on, () => authorRef.current || "You", hist);
    setHistoryHook((c) => { if (!on()) return false; c === "undo" ? hist.undo() : hist.redo(); return true; });
    const keys = (e) => {
      if (!on() || !(e.ctrlKey || e.metaKey)) return;
      const k = e.key.toLowerCase();
      if (k === "z" && !e.shiftKey) { e.preventDefault(); hist.undo(); }
      else if (k === "y" || (k === "z" && e.shiftKey)) { e.preventDefault(); hist.redo(); }
    };
    el.addEventListener("beforeinput", h);
    el.addEventListener("keydown", keys);
    return () => { el.removeEventListener("beforeinput", h); el.removeEventListener("keydown", keys); setHistoryHook(null); };
  }, [body]);

  const toggleTrack = () => {
    const on = !settingsRef.current.trackChanges;
    setSettings({ ...settingsRef.current, trackChanges: on });
    toast(on ? `Track changes on — edits are marked as ${authorRef.current}` : "Track changes off");
  };
  const resolveAll = (accept) => {
    const list = changesIn(bodyRef.current);
    if (!list.length) return toast("No tracked changes");
    list.forEach((el) => resolveChange(el, accept));
    bodyRef.current.normalize();
    markDirty();
    toast.success(`${accept ? "Accepted" : "Rejected"} ${list.length} change${list.length > 1 ? "s" : ""}`);
  };

  const currentBody = useCallback(() => ({
    html: bodyRef.current.innerHTML.replace(/ ?img-selected/g, "").replace(/<span class="pdf-marker"[^>]*><\/span>/g, ""),
    settings: { ...settingsRef.current, header: headerRef.current.innerHTML, footer: footerRef.current.innerHTML },
  }), []);

  const save = useCallback(async (manual) => {
    if (!recRef.current || !bodyRef.current) return;
    dirtyRef.current = false;
    setStatus("saving");
    let r;
    try {
      r = await writeDoc({ ...recRef.current, title: titleRef.current.trim() || "Untitled document" }, currentBody(), pwRef.current);
    } catch (x) {
      dirtyRef.current = true;
      setStatus("dirty");
      toast.error(`Could not save: ${x.message || "browser storage error"}`);
      return;
    }
    recRef.current = r;
    setRec(r);
    setLastSaved(r.updatedAt);
    setStatus(dirtyRef.current ? "dirty" : "saved");
    if (manual) toast.success(r.encrypted ? "Saved & encrypted" : "Document saved");
  }, [currentBody]);

  useEffect(() => {
    if (!body || !autosaveMin) return;
    const t = setInterval(() => { if (dirtyRef.current) save(false); }, autosaveMin * 60000);
    return () => clearInterval(t);
  }, [body, autosaveMin, save]);

  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 20000); return () => clearInterval(t); }, []);

  useEffect(() => {
    const key = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const k = e.key.toLowerCase();
      if (k === "s") { e.preventDefault(); save(true); }
      if (k === "f" || k === "h") { e.preventDefault(); setPanel("find"); }
    };
    const unload = (e) => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("keydown", key);
    window.addEventListener("beforeunload", unload);
    return () => { window.removeEventListener("keydown", key); window.removeEventListener("beforeunload", unload); };
  }, [save]);

  useEffect(() => {
    const h = () => {
      const c = currentCell();
      setInTable(!!c && !!bodyRef.current?.contains(c));
    };
    document.addEventListener("selectionchange", h);
    return () => document.removeEventListener("selectionchange", h);
  }, []);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const change = (e) => {
      const t = e.target;
      if (t.tagName === "SELECT") [...t.options].forEach((o, i) => (i === t.selectedIndex ? o.setAttribute("selected", "") : o.removeAttribute("selected")));
      if (t.type === "checkbox") t.checked ? t.setAttribute("checked", "") : t.removeAttribute("checked");
      else if (t.tagName === "INPUT") t.setAttribute("value", t.value);
      markDirty();
    };
    el.addEventListener("change", change);
    return () => el.removeEventListener("change", change);
  }, [body, markDirty]);

  const selectImg = (img) => {
    bodyRef.current?.querySelectorAll(".img-selected").forEach((i) => i.classList.remove("img-selected"));
    if (img) img.classList.add("img-selected");
    setSelImg(img);
  };

  const clearMarker = () => bodyRef.current?.querySelectorAll(".pdf-marker").forEach((m) => m.remove());

  const makeOverlay = (page, x, y, kind) => {
    const el = document.createElement("span");
    el.setAttribute("contenteditable", "false");
    el.style.left = `${x}%`;
    el.style.top = `${y}%`;
    if (kind === "box") {
      el.className = "pdf-overlay";
      el.innerHTML = `${OV_UI}<span class="ov-box"></span>`;
    } else if (kind === "sig") {
      el.className = "pdf-overlay sig-field";
      el.innerHTML = OV_UI + SIG_PLACEHOLDER;
    } else if (kind === "initials") {
      el.className = "pdf-overlay sig-field sig-initials";
      el.dataset.kind = "initials";
      el.innerHTML = OV_UI + INITIALS_PLACEHOLDER;
    } else {
      const text = kind === "date" ? new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "Type here";
      el.className = "pdf-overlay";
      el.innerHTML = `${OV_UI}<span class="ov-text" contenteditable="true">${esc(text)}</span>`;
    }
    page.appendChild(el);
    return el;
  };

  const placeOverlay = (kind) => {
    const { page, x, y } = pdfPoint;
    clearMarker();
    const el = kind === "initials-all"
      ? [...bodyRef.current.querySelectorAll(".pdf-page")].map((pg) => makeOverlay(pg, x, y, "initials")).find((o) => o.closest(".pdf-page") === page)
      : makeOverlay(page, x, y, kind);
    setPdfPoint(null);
    markDirty();
    if (el.classList.contains("sig-field")) setSigTarget(el);
    if (kind === "text") {
      const t = el.querySelector(".ov-text");
      t.focus();
      const r = document.createRange();
      r.selectNodeContents(t);
      window.getSelection().removeAllRanges();
      window.getSelection().addRange(r);
    }
  };

  const startDrag = (e, ov) => {
    e.preventDefault();
    const page = ov.closest(".pdf-page");
    const pr = page.getBoundingClientRect();
    const or = ov.getBoundingClientRect();
    const dx = e.clientX - or.left, dy = e.clientY - or.top;
    const move = (ev) => {
      ov.style.left = `${Math.max(0, Math.min(97, ((ev.clientX - dx - pr.left) / pr.width) * 100)).toFixed(2)}%`;
      ov.style.top = `${Math.max(0, Math.min(97, ((ev.clientY - dy - pr.top) / pr.height) * 100)).toFixed(2)}%`;
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); markDirty(); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const handlers = {
    onPointerDown: (e) => {
      const g = e.target.closest(".ov-grip");
      if (g) startDrag(e, g.closest(".pdf-overlay"));
    },
    onKeyDown: (e) => {
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        const sel = window.getSelection();
        const n = sel.anchorNode;
        const span = n?.nodeType === 3 && n.parentElement.closest(".cmt");
        if (sel.isCollapsed && span && sel.anchorOffset === n.data.length && span.lastChild === n) {
          const z = document.createTextNode("\u200B");
          span.after(z);
          const r = document.createRange();
          r.setStart(z, 1);
          r.collapse(true);
          sel.removeAllRanges();
          sel.addRange(r);
        }
      }
      if (e.key !== "Tab") return;
      e.preventDefault();
      const sel = window.getSelection();
      const n = sel.anchorNode?.nodeType === 3 ? sel.anchorNode.parentElement : sel.anchorNode;
      if (n?.closest?.("li")) exec(e.shiftKey ? "outdent" : "indent");
      else if (!e.shiftKey) insertText("\t");
    },
    onClick: (e) => {
      if (e.target.closest(".ov-grip")) return;
      const del = e.target.closest(".ov-del");
      if (del) { del.closest(".pdf-overlay").remove(); markDirty(); return; }
      const page = e.target.closest(".pdf-page");
      if (page && !e.target.closest(".pdf-overlay")) {
        const r = page.getBoundingClientRect();
        const x = ((e.clientX - r.left) / r.width) * 100, y = ((e.clientY - r.top) / r.height) * 100;
        clearMarker();
        const m = document.createElement("span");
        m.className = "pdf-marker";
        m.style.left = `${x}%`;
        m.style.top = `${y}%`;
        page.appendChild(m);
        setPdfPoint({ page, x: Math.min(x, 85), y: Math.min(y, 92), n: page.dataset.page });
        return;
      }
      if (pdfPoint) { clearMarker(); setPdfPoint(null); }
      const cm = e.target.closest(".cmt");
      if (cm) { setActiveCmt(cm.dataset.cid); setPanel("comments"); }
      const sig = e.target.closest(".sig-field");
      if (sig) {
        e.preventDefault();
        const wait = canSign(sig, bodyRef.current, settingsRef.current);
        if (wait) return toast(`Waiting for ${wait} to sign first`);
        return setSigTarget(sig);
      }
      if (e.target.tagName === "IMG") return selectImg(e.target);
      selectImg(null);
      const a = e.target.closest("a");
      if (a && (e.ctrlKey || e.metaKey)) window.open(a.href, "_blank", "noopener");
    },
    onPaste: async (e) => {
      const f = [...(e.clipboardData?.files || [])].find((x) => x.type.startsWith("image/"));
      if (!f) return;
      e.preventDefault();
      insertImage(await readFile(f));
    },
  };

  const pruneComments = useCallback(() => {
    const list = settingsRef.current?.comments || [];
    const empty = list.filter((c) => !c.text);
    if (!empty.length || !bodyRef.current) return;
    empty.forEach((c) => unwrapComment(bodyRef.current, c.id));
    const s = { ...settingsRef.current, comments: list.filter((c) => c.text) };
    settingsRef.current = s;
    setSettingsState(s);
  }, []);
  useEffect(() => { if (panel !== "comments") pruneComments(); }, [panel, pruneComments]);

  const logoInput = useRef();
  const logoTarget = useRef(null);
  const insertLogo = () => {
    const h = headerRef.current;
    const el = document.createElement("span");
    el.className = "logo-field";
    el.setAttribute("contenteditable", "false");
    el.textContent = "LOGO";
    h.prepend(el, document.createTextNode("\u00a0 "));
    markDirty();
    h.scrollIntoView({ block: "center", behavior: "smooth" });
    logoTarget.current = el;
    logoInput.current.click();
  };
  const insertDigital = async () => {
    const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(bodyRef.current.innerText));
    const hash = [...new Uint8Array(buf)].slice(0, 8).map((b) => b.toString(16).padStart(2, "0")).join("").toUpperCase();
    exec("insertHTML", `<span class="digi-badge" contenteditable="false"><b>✔ Digitally signed document</b><span>Signed by ${esc(author)} · ${esc(new Date().toLocaleString())}</span><span>Document ID ${hash}</span></span>&nbsp;`);
  };

  const addComment = () => {
    pruneComments();
    const id = Math.random().toString(36).slice(2, 10);
    if (!wrapRange("cmt", { cid: id })) return toast("Select some text in the page first, then add a comment");
    setSettings({ ...settingsRef.current, comments: [...(settingsRef.current.comments || []), { id, text: "", author, createdAt: Date.now(), resolved: false, replies: [] }] });
    setActiveCmt(id);
    setPanel("comments");
  };

  const openDialog = (name, tab) => { setDialog(name); setDialogTab(tab || null); };
  const closeDialog = () => { setDialog(null); setDialogTab(null); };

  const exportAs = async (fmt) => {
    setBusy(`Preparing .${fmt}…`);
    try {
      const b = currentBody();
      const t = titleRef.current || "Untitled document";
      const blob = fmt === "zip" ? await zipDocs([{ title: t, ...b }], ["docx", "pdf", "txt"]) : await buildBlob(fmt, b.html, b.settings);
      download(blob, `${safeName(t)}.${fmt}`);
    } catch (x) { toast.error(x.message || "Export failed"); }
    setBusy("");
  };

  const back = async () => {
    pruneComments(); if (dirtyRef.current) await save(false); nav("/"); };

  if (locked) {
    return (
      <div className="h-screen bg-muted/40 flex items-center justify-center" data-testid="editor-locked">
        <PasswordPrompt open title={rec?.title} onCancel={() => nav("/")}
          onSubmit={async (pw) => { const b = await decryptJSON(rec.cipher, pw); pwRef.current = pw; setLocked(false); openBody(b); }} />
      </div>
    );
  }
  if (!settings) return <div className="h-screen flex items-center justify-center text-muted-foreground" data-testid="editor-loading"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  const statusText = status === "saving" ? "Saving…" : status === "dirty" ? "Unsaved changes" : lastSaved ? `Saved ${formatDistanceToNow(lastSaved, { addSuffix: true })}` : "Saved";

  return (
    <div className="h-screen flex flex-col bg-background" data-testid="editor-page">
      <header className="h-14 shrink-0 border-b bg-card flex items-center gap-2 sm:gap-3 px-2 sm:px-3 min-w-0">
        <Button variant="ghost" size="icon" onClick={back} data-testid="editor-back-btn" title="Back to library"><ArrowLeft className="h-4 w-4" /></Button>
        <div className="hidden sm:flex h-8 w-8 rounded-md bg-primary/10 text-primary items-center justify-center shrink-0"><FileText className="h-4 w-4" /></div>
        <input value={title} onChange={(e) => { setTitle(e.target.value); titleRef.current = e.target.value; markDirty(); }}
          className="min-w-[110px] flex-1 max-w-md bg-transparent text-[15px] font-semibold outline-none rounded px-1.5 py-1 hover:bg-accent focus:bg-accent" data-testid="editor-title-input" aria-label="Document title" />
        {rec?.encrypted && <span className="hidden sm:flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-amber-600 bg-amber-500/10 rounded px-2 py-1" data-testid="editor-protected-badge"><Lock className="h-3 w-3" />Protected</span>}
        <div className="ml-auto flex items-center gap-1.5 sm:gap-2 shrink-0">
          <span className={`hidden lg:flex items-center gap-1.5 text-xs ${status === "dirty" ? "text-amber-600" : "text-muted-foreground"}`} data-testid="autosave-status">
            {status === "saving" ? <Loader2 className="h-3 w-3 animate-spin" /> : status === "saved" ? <Check className="h-3 w-3" /> : <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />}{statusText}
          </span>
          <Button size="sm" variant="outline" onClick={() => save(true)} data-testid="editor-save-btn"><Save className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Save</span></Button>
          <Button size="sm" variant="outline" onClick={() => openDialog("protect")} data-testid="editor-protect-btn">{rec?.encrypted ? <Lock className="h-4 w-4 sm:mr-1.5 text-amber-600" /> : <LockOpen className="h-4 w-4 sm:mr-1.5" />}<span className="hidden sm:inline">Password</span></Button>
          <Button size="sm" variant="outline" onClick={() => openDialog("preview")} data-testid="editor-preview-btn"><Printer className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Preview</span></Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button size="sm" data-testid="editor-export-btn"><Download className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Export</span><ChevronDown className="h-3.5 w-3.5 ml-1 hidden sm:block" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end" onCloseAutoFocus={(e) => e.preventDefault()}>
              <DropdownMenuItem onClick={() => exportAs("docx")} data-testid="export-docx-option">Word document (.docx)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportAs("pdf")} data-testid="export-pdf-option">PDF (.pdf)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportAs("txt")} data-testid="export-txt-option">Plain text (.txt)</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => exportAs("zip")} data-testid="export-zip-option">ZIP (docx + pdf + txt)</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => openDialog("template")} data-testid="save-as-template-option">Save as template…</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <ThemeToggle />
        </div>
      </header>

      <Ribbon settings={settings} setSettings={setSettings} openDialog={openDialog} openPanel={setPanel} addComment={addComment} insertLogo={insertLogo} insertDigital={insertDigital} tracking={!!settings.trackChanges} toggleTrack={toggleTrack} resolveAll={resolveAll}
        focusHeader={() => { headerRef.current.focus(); headerRef.current.scrollIntoView({ block: "center", behavior: "smooth" }); setActiveRegion(headerRef.current); }}
        words={counts.words} chars={counts.chars} />
      {inTable && !selImg && <TableTools />}
      {selImg && <ImageTools img={selImg} onDone={() => setSelImg(null)} />}

      <div className="flex-1 flex min-h-0 relative" onInput={markDirty}
        onClick={(e) => { const l = e.target.closest(".logo-field"); if (l) { logoTarget.current = l; logoInput.current.click(); } }}>
        <input ref={logoInput} type="file" accept="image/*" hidden data-testid="logo-file-input" onChange={async (e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (!f || !logoTarget.current) return;
          logoTarget.current.innerHTML = `<img src="${await readFile(f)}" alt="Logo">`;
          markDirty();
        }} />
        {pdfPoint && <div className="absolute top-0 left-0 right-0 z-20 shadow-md"><PdfTools page={pdfPoint.n} onPlace={placeOverlay} onCancel={() => { clearMarker(); setPdfPoint(null); }} /></div>}
        <Canvas settings={settings} bodyRef={bodyRef} headerRef={headerRef} footerRef={footerRef} zoom={zoom} handlers={handlers} total={counts.pages} />
        <div className="lg:hidden flex flex-col gap-1 border-l bg-card p-1" data-testid="panel-rail">
          {[["find", Search, "Find"], ["comments", MessageSquare, "Comments"], ["changes", GitCompareArrows, "Changes"], ["sign", ListChecks, "Signing"], ["ai", Sparkles, "AI"]].map(([k, I, l]) => (
            <button key={k} onClick={() => setPanel(panel === k ? null : k)} title={l} aria-label={l} data-testid={`rail-${k}`}
              className={`h-10 w-10 rounded-md flex items-center justify-center ${panel === k ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent"}`}><I className="h-4 w-4" /></button>
          ))}
        </div>
        {panel && (
          <div className="max-lg:fixed max-lg:inset-0 max-lg:z-40 max-lg:bg-black/30 lg:contents" data-testid="panel-drawer" onClick={(e) => e.target === e.currentTarget && setPanel(null)}>
            <div className="h-full flex max-lg:absolute max-lg:right-0 max-lg:top-0 max-lg:w-[88vw] max-lg:max-w-[360px] max-lg:shadow-2xl max-lg:animate-in max-lg:slide-in-from-right max-lg:[&>aside]:w-full">
        {panel === "find" && <FindReplace rootRef={bodyRef} onClose={() => setPanel(null)} />}
        {panel === "sign" && <SigningPanel rootRef={bodyRef} settings={settings} setSettings={setSettings} onSign={setSigTarget} onClose={() => setPanel(null)} />}
        {panel === "changes" && <ChangesPanel rootRef={bodyRef} tracking={!!settings.trackChanges} onToggle={toggleTrack} onClose={() => setPanel(null)} onChanged={markDirty} />}
              {panel === "ai" && <AiPanel rootRef={bodyRef} onClose={() => setPanel(null)} />}
            </div>
          </div>
        )}
        {panel === "comments" && <CommentsPanel rootRef={bodyRef} comments={settings.comments || []} setComments={(c) => setSettings({ ...settingsRef.current, comments: c })}
          activeId={activeCmt} setActiveId={setActiveCmt} onClose={() => setPanel(null)} author={author}
          setAuthor={(a) => { setAuthorState(a); localStorage.setItem("lw.author", a); }} />}
      </div>

      <footer className="h-8 shrink-0 border-t bg-card flex items-center gap-3 sm:gap-4 px-3 sm:px-4 text-xs text-muted-foreground whitespace-nowrap overflow-hidden">
        <span data-testid="status-pages">~{counts.pages} page{counts.pages > 1 ? "s" : ""}</span>
        <span data-testid="status-words">{counts.words} words</span>
        <button className="hidden sm:flex items-center gap-1 hover:text-foreground" onClick={() => setPanel((p) => (p === "find" ? null : "find"))} data-testid="status-find-btn"><Search className="h-3 w-3" />Find</button>
        {settings.trackChanges && <button className="flex items-center gap-1 text-emerald-700 font-medium hover:underline" onClick={() => setPanel("changes")} data-testid="status-tracking-btn"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />Tracking</button>}
        {counts.sigTodo > 0 && <button className="flex items-center gap-1 text-primary font-medium hover:underline" onClick={() => setPanel("sign")} data-testid="status-sign-btn"><PenLine className="h-3 w-3" />{counts.sigTodo} to sign</button>}
        {(settings.comments || []).some((c) => !c.resolved) && <button className="hidden sm:flex items-center gap-1 text-amber-600 hover:underline" onClick={() => setPanel("comments")} data-testid="status-comments-btn"><MessageSquare className="h-3 w-3" />{settings.comments.filter((c) => !c.resolved).length} comment{settings.comments.filter((c) => !c.resolved).length > 1 ? "s" : ""}</button>}
        <label className="hidden sm:flex items-center gap-1.5">Autosave
          <select className="bg-transparent border rounded px-1 py-0.5 text-xs" value={autosaveMin} data-testid="editor-autosave-select"
            onChange={(e) => { const m = Number(e.target.value); setAutosaveMin(m); setAutosaveMinutes(m); }}>
            {[1, 2, 5, 10, 15, 30].map((m) => <option key={m} value={m}>every {m} min</option>)}
            <option value={0}>off</option>
          </select>
        </label>
        <div className="ml-auto flex items-center gap-4">
          <InfoWidget compact />
          <div className="flex items-center gap-1">
            <button onClick={() => setZoom((z) => Math.max(0.3, +(z - 0.1).toFixed(2)))} className="hover:text-foreground" data-testid="zoom-out-btn"><Minus className="h-3 w-3" /></button>
            <span className="w-9 text-center tabular-nums" data-testid="zoom-level">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom((z) => Math.min(2, +(z + 0.1).toFixed(2)))} className="hover:text-foreground" data-testid="zoom-in-btn"><Plus className="h-3 w-3" /></button>
            <button onClick={fitWidth} className="ml-1 rounded border px-1.5 hover:text-foreground" title="Fit page to screen width" data-testid="zoom-fit-btn">Fit</button>
          </div>
          <span className="hidden sm:inline" data-testid="editor-version">Version {APP_VERSION}</span>
        </div>
      </footer>

      {busy && <div className="fixed bottom-12 right-5 z-50 lw-card px-4 py-3 flex items-center gap-2 text-sm" data-testid="busy-indicator"><Loader2 className="h-4 w-4 animate-spin text-primary" />{busy}</div>}
      <LinkDialog open={dialog === "link"} onClose={closeDialog} />
      <PayLinkDialog open={dialog === "paylink"} onClose={closeDialog} rootRef={bodyRef} title={title} />
      <SavedFieldsDialog open={dialog === "savedfields"} onClose={closeDialog} regions={() => [headerRef.current, bodyRef.current, footerRef.current]} />
      <FieldPicker />
      <DropdownFieldDialog open={dialog === "dropdown"} onClose={closeDialog} />
      <SaveTemplateDialog open={dialog === "template"} onClose={closeDialog} defaultName={title} encrypted={!!rec?.encrypted}
        onSave={async (name, desc, blank, folder) => {
          const b = currentBody();
          const t = document.createElement("div");
          t.innerHTML = b.html;
          if (blank) t.querySelectorAll('.sig-field[data-signed="1"]').forEach((f) => {
            const ui = f.querySelector(":scope > .ov-ui");
            f.innerHTML = (ui ? ui.outerHTML : "") + (f.dataset.kind === "initials" ? INITIALS_PLACEHOLDER : SIG_PLACEHOLDER);
            f.removeAttribute("data-signed");
            f.removeAttribute("data-signer");
          });
          t.querySelectorAll(".cmt").forEach((n) => n.replaceWith(...n.childNodes));
          await saveTemplate(name, desc, t.innerHTML, { ...b.settings, comments: [] }, folder); toast.success(`Template “${name}” saved — find it on the library page`); }} />
      <ImageDialog tab={dialog === "image" ? dialogTab || "upload" : null} onTab={setDialogTab} onClose={closeDialog} />
      <PageSetupDialog tab={dialog === "pagesetup" ? dialogTab || "paper" : null} onTab={setDialogTab} onClose={closeDialog} settings={settings} setSettings={setSettings} />
      <ProtectDialog open={dialog === "protect"} onClose={closeDialog} hasPassword={!!pwRef.current} currentPw={pwRef.current}
        onApply={async (pw) => { pwRef.current = pw; await save(false); }} />
      <PrintPreview open={dialog === "preview"} onClose={closeDialog} getBody={currentBody} title={title} />
      <SignatureDialog target={sigTarget} onClose={() => setSigTarget(null)} />
    </div>
  );
}
